/**
 * SwipeableContent
 * 
 * Wraps page content to enable horizontal swipe navigation between pages
 * while allowing vertical scrolling within each page.
 * 
 * Only active on mobile devices (< 1024px).
 */

import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface SwipeableContentProps {
  children: React.ReactNode;
}

// Define the main navigation pages in order
const NAV_PAGES = [
  { path: '/', name: 'Workspace' },
  { path: '/knowledge', name: 'Knowledge' },
  { path: '/profile', name: 'Profile' },
];

export const SwipeableContent: React.FC<SwipeableContentProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;
  // Maximum time for a swipe gesture (in ms)
  const maxSwipeTime = 300;

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    });
    setIsScrolling(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const touch = e.targetTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    
    // If vertical movement is greater than horizontal, user is scrolling vertically
    if (deltaY > deltaX && deltaY > 10) {
      setIsScrolling(true);
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || isScrolling) {
      setTouchStart(null);
      setIsScrolling(false);
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touchStart.x - touch.clientX;
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const deltaTime = Date.now() - touchStart.time;

    // Only process swipe if:
    // 1. Horizontal movement is significant
    // 2. Vertical movement is minimal (not scrolling)
    // 3. Swipe was fast enough
    if (
      Math.abs(deltaX) > minSwipeDistance &&
      deltaY < minSwipeDistance &&
      deltaTime < maxSwipeTime
    ) {
      const currentIndex = NAV_PAGES.findIndex((page) => page.path === location.pathname);
      
      if (deltaX > 0 && currentIndex < NAV_PAGES.length - 1) {
        // Swipe left - go to next page
        navigate(NAV_PAGES[currentIndex + 1].path);
      } else if (deltaX < 0 && currentIndex > 0) {
        // Swipe right - go to previous page
        navigate(NAV_PAGES[currentIndex - 1].path);
      }
    }

    setTouchStart(null);
    setIsScrolling(false);
  };

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-y-auto overflow-x-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        // Allow vertical scrolling, but handle horizontal swipes
        touchAction: 'pan-y',
      }}
    >
      {children}
    </div>
  );
};

export default SwipeableContent;

